import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  ArrowRight,
  Check,
  EyeOff,
  PenLine,
  RefreshCw,
  Sparkles,
  UserRound,
  UsersRound,
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import LockWords from '../../components/reflect/LockWords';
import SongResults from '../../components/reflect/SongResults';
import StoryCard from '../../components/reflect/StoryCard';
import {
  CareMessage,
  ErrorMessage,
  LoadingScreen,
  PinKeypad,
  PickedStudent,
  Pizzul,
  ReflectShell,
  SavedMark,
  StepHeading,
} from '../../components/reflect/StudentReflectUI';
import { API_URL } from '../../utils/api';
import { vibeStyle } from '../../utils/vibeStyle';
import {
  apiErrorMessage,
  calculateResumeDestination,
  rewriteName,
  shouldShowCareMessage,
} from '../../utils/reflectFlow';
import './StudentReflect.css';

const STORAGE = {
  token: 'sp_reflect_token',
  classId: 'sp_reflect_class_id',
  alias: 'sp_reflect_alias',
  sessionId: 'sp_reflect_session_id',
};

const VIBES = [
  ['big-energy', 'Big Energy'],
  ['bounce-vibe', 'Bounce Vibe'],
  ['calm-focus', 'Calm Focus'],
  ['trap-energy', 'Trap Energy'],
  ['afro-chill', 'Afro Chill'],
  ['warm-rnb', 'Warm R&B'],
  ['dark-drill', 'Dark Drill'],
  ['latin-pop', 'Latin Pop'],
  ['latin-warm', 'Latin Warm'],
  ['reggaeton-bounce', 'Reggaeton Bounce'],
  ['playful-pop', 'Playful Pop'],
  ['confident-grind', 'Confident Grind'],
];

const SWAP_SUGGESTIONS = ['my friend', 'my cousin', 'my coach', 'my sister', 'my brother'];
const WRITING_LINES = [
  'Pizzul is listening for your voice.',
  'Your locked words are staying close.',
  'Turning your answers into lines.',
  'Finding the heart of your story.',
];

class ReflectApiError extends Error {
  constructor(status, detail, context) {
    super(apiErrorMessage(status, detail, context));
    this.status = status;
    this.detail = detail;
  }
}

const reflectRequest = async (path, options = {}, token = '', context = '') => {
  const response = await fetch(`${API_URL}${path}`, {
    ...options,
    headers: {
      ...(options.body ? { 'Content-Type': 'application/json' } : {}),
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(options.headers || {}),
    },
  });
  let data = {};
  try {
    data = await response.json();
  } catch (_error) {
    data = {};
  }
  if (!response.ok) throw new ReflectApiError(response.status, data.detail, context);
  return data;
};

const parseLocks = (value) => {
  if (Array.isArray(value)) return value;
  try {
    const parsed = JSON.parse(value || '[]');
    return Array.isArray(parsed) ? parsed : [];
  } catch (_error) {
    return [];
  }
};

const answerFromServer = (answer = {}) => ({
  id: answer.id || null,
  text: answer.answer_text || answer.text || '',
  skipped: Boolean(answer.skipped),
  private: Boolean(answer.private),
  locked_phrases: parseLocks(answer.locked_phrases),
  revision: 0,
  dirty: false,
  saved: true,
});

const blankAnswer = () => ({
  id: null,
  text: '',
  skipped: false,
  private: false,
  locked_phrases: [],
  revision: 0,
  dirty: false,
  saved: false,
});

const statusCopy = {
  unclaimed: 'Ready to claim',
  pending: 'Waiting for teacher',
  approved: 'Ready to sign in',
};

const StudentReflect = () => {
  const [booting, setBooting] = useState(true);
  const [step, setStep] = useState('join');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [joinCode, setJoinCode] = useState('');
  const [classroom, setClassroom] = useState(null);
  const [rosterEntry, setRosterEntry] = useState(null);
  const [autoApproved, setAutoApproved] = useState(false);
  const [alias, setAlias] = useState('');
  const [pin, setPin] = useState('');
  const [pinConfirm, setPinConfirm] = useState('');
  const [token, setToken] = useState('');
  const [classId, setClassId] = useState('');
  const [themes, setThemes] = useState([]);
  const [session, setSession] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [answers, setAnswers] = useState({});
  const [questionIndex, setQuestionIndex] = useState(0);
  const [savingQuestion, setSavingQuestion] = useState('');
  const [questionError, setQuestionError] = useState('');
  const [possibleNames, setPossibleNames] = useState([]);
  const [selectedVibe, setSelectedVibe] = useState('');
  const [justWriteMode, setJustWriteMode] = useState(false);
  const [draft, setDraft] = useState(null);
  const [writingLine, setWritingLine] = useState(0);
  const [writingError, setWritingError] = useState('');
  const [care, setCare] = useState(null);
  const careTimer = useRef(null);
  const careShownQuestions = useRef(new Set());
  const careShownSheets = useRef(new Set());

  const studentRequest = useCallback((path, options = {}, context = '') => (
    reflectRequest(path, options, token, context)
  ), [token]);

  const clearStudentStorage = useCallback(() => {
    Object.values(STORAGE).forEach((key) => sessionStorage.removeItem(key));
  }, []);

  const hydrateSession = useCallback(async (storedToken, storedClassId, storedSessionId) => {
    const loadedSession = await reflectRequest(
      `/api/reflect/classes/${storedClassId}/sessions/${storedSessionId}`,
      {},
      storedToken,
    );
    const loadedQuestions = await reflectRequest(
      `/api/reflect/classes/${storedClassId}/questions?theme=${encodeURIComponent(loadedSession.theme_key)}`,
      {},
      storedToken,
    );
    const loadedAnswers = {};
    (loadedSession.answers || []).forEach((answer) => {
      loadedAnswers[answer.question_id] = answerFromServer(answer);
    });
    let existingDraft = null;
    try {
      existingDraft = await reflectRequest(
        `/api/reflect/classes/${storedClassId}/sessions/${storedSessionId}/lyrics`,
        {},
        storedToken,
      );
    } catch (requestError) {
      if (requestError.status !== 404) throw requestError;
    }
    const destination = calculateResumeDestination(
      loadedQuestions,
      loadedSession.answers || [],
      existingDraft,
    );
    setSession(loadedSession);
    setJustWriteMode(loadedSession.theme_key === 'just_write');
    setQuestions(loadedQuestions);
    setAnswers(loadedAnswers);
    if (destination.questionIndex !== null) setQuestionIndex(destination.questionIndex);
    if (existingDraft) setDraft(existingDraft);
    setStep(destination.step);
  }, []);

  useEffect(() => {
    let active = true;
    const resume = async () => {
      const storedToken = sessionStorage.getItem(STORAGE.token) || '';
      const storedClassId = sessionStorage.getItem(STORAGE.classId) || '';
      const storedAlias = sessionStorage.getItem(STORAGE.alias) || '';
      if (!storedToken || !storedClassId) {
        if (active) setBooting(false);
        return;
      }
      setToken(storedToken);
      setClassId(storedClassId);
      setAlias(storedAlias);
      try {
        const loadedThemes = await reflectRequest(
          `/api/reflect/themes?class_id=${encodeURIComponent(storedClassId)}`,
          {},
          storedToken,
        );
        if (!active) return;
        setThemes(loadedThemes);
        const storedSessionId = sessionStorage.getItem(STORAGE.sessionId);
        if (storedSessionId) await hydrateSession(storedToken, storedClassId, storedSessionId);
        else setStep('themes');
      } catch (_resumeError) {
        clearStudentStorage();
        if (active) {
          setToken('');
          setClassId('');
          setStep('join');
        }
      } finally {
        if (active) setBooting(false);
      }
    };
    resume();
    return () => { active = false; };
  }, [clearStudentStorage, hydrateSession]);

  useEffect(() => () => clearTimeout(careTimer.current), []);

  useEffect(() => {
    if (step !== 'writing') return undefined;
    const interval = setInterval(() => {
      setWritingLine((line) => (line + 1) % WRITING_LINES.length);
    }, 2400);
    return () => clearInterval(interval);
  }, [step]);

  const showCareMessage = useCallback((afterContinue = null) => {
    clearTimeout(careTimer.current);
    setCare({ canContinue: false, afterContinue });
    careTimer.current = setTimeout(() => {
      setCare((current) => current ? { ...current, canContinue: true } : current);
    }, 5000);
  }, []);

  const closeCareMessage = () => {
    if (!care?.canContinue) return;
    const next = care.afterContinue;
    setCare(null);
    if (next) next();
  };

  const lookupClass = async (event) => {
    event.preventDefault();
    if (joinCode.length !== 6) {
      setError('Enter all 6 characters from your teacher.');
      return;
    }
    setBusy(true);
    setError('');
    try {
      const data = await reflectRequest(`/api/reflect/join/${joinCode}`, {}, '', 'join');
      setClassroom(data);
      setStep('roster');
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setBusy(false);
    }
  };

  const chooseRosterEntry = (entry) => {
    setRosterEntry(entry);
    setError('');
    setPin('');
    if (entry.status === 'unclaimed') setStep('alias');
    else if (entry.status === 'approved') setStep('sign-in');
    else setError('That name is waiting for teacher approval.');
  };

  const claimProfile = async () => {
    if (pin !== pinConfirm) {
      setError('Those PINs do not match yet. Try the second one again.');
      return;
    }
    setBusy(true);
    setError('');
    try {
      const claimed = await reflectRequest('/api/reflect/claim', {
        method: 'POST',
        body: JSON.stringify({ join_code: joinCode, roster_id: rosterEntry.id, alias, pin }),
      });
      // Demo and practice classes can auto-approve; the waiting screen then signs in at once.
      setAutoApproved(claimed?.status === 'approved');
      setStep('waiting');
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setBusy(false);
    }
  };

  const finishSignIn = useCallback(async (data) => {
    sessionStorage.setItem(STORAGE.token, data.token);
    sessionStorage.setItem(STORAGE.classId, data.class_id);
    sessionStorage.setItem(STORAGE.alias, data.alias);
    sessionStorage.removeItem(STORAGE.sessionId);
    setToken(data.token);
    setClassId(data.class_id);
    setAlias(data.alias);
    const loadedThemes = await reflectRequest(
      `/api/reflect/themes?class_id=${encodeURIComponent(data.class_id)}`,
      {},
      data.token,
    );
    setThemes(loadedThemes);
    setStep('themes');
  }, []);

  const signIn = useCallback(async (showErrors = true) => {
    if (!rosterEntry || pin.length !== 4) return false;
    if (showErrors) {
      setBusy(true);
      setError('');
    }
    try {
      const data = await reflectRequest('/api/reflect/sign-in', {
        method: 'POST',
        body: JSON.stringify({ join_code: joinCode, roster_id: rosterEntry.id, pin }),
      }, '', 'sign-in');
      await finishSignIn(data);
      return true;
    } catch (requestError) {
      if (showErrors || requestError.status !== 403) setError(requestError.message);
      return false;
    } finally {
      if (showErrors) setBusy(false);
    }
  }, [finishSignIn, joinCode, pin, rosterEntry]);

  useEffect(() => {
    if (step !== 'waiting') return undefined;
    let active = true;
    let timer;
    const poll = async () => {
      const signedIn = await signIn(false);
      if (active && !signedIn) timer = setTimeout(poll, 6000);
    };
    timer = setTimeout(poll, autoApproved ? 0 : 6000);
    return () => {
      active = false;
      clearTimeout(timer);
    };
  }, [autoApproved, signIn, step]);

  const startTheme = async (theme) => {
    setBusy(true);
    setError('');
    try {
      const loadedQuestions = await studentRequest(
        `/api/reflect/classes/${classId}/questions?theme=${encodeURIComponent(theme.key)}`,
      );
      const started = await studentRequest(`/api/reflect/classes/${classId}/sessions`, {
        method: 'POST',
        body: JSON.stringify({ theme_key: theme.key }),
      });
      const nextSession = { id: started.id, theme_key: theme.key, theme_title: theme.title };
      sessionStorage.setItem(STORAGE.sessionId, started.id);
      setSession(nextSession);
      setJustWriteMode(false);
      setQuestions(loadedQuestions);
      setAnswers({});
      careShownQuestions.current = new Set();
      setQuestionIndex(0);
      setStep('questions');
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setBusy(false);
    }
  };

  const currentQuestion = questions[questionIndex];
  const currentAnswer = currentQuestion ? (answers[currentQuestion.id] || blankAnswer()) : blankAnswer();
  const totalLocks = useMemo(() => (
    Object.values(answers).reduce((count, answer) => count + (answer.locked_phrases?.length || 0), 0)
  ), [answers]);

  const updateAnswer = (questionId, changes) => {
    setAnswers((current) => {
      const previous = current[questionId] || blankAnswer();
      return {
        ...current,
        [questionId]: {
          ...previous,
          ...changes,
          revision: previous.revision + 1,
          dirty: true,
          saved: false,
        },
      };
    });
    setQuestionError('');
  };

  const saveAnswer = useCallback(async (question, snapshot, options = {}) => {
    if (!question || !session) return null;
    const revision = snapshot.revision;
    setSavingQuestion(question.id);
    if (!options.quiet) setQuestionError('');
    try {
      const result = await studentRequest(
        `/api/reflect/classes/${classId}/sessions/${session.id}/answers/${question.id}`,
        {
          method: 'PUT',
          body: JSON.stringify({
            text: snapshot.text || '',
            skipped: Boolean(snapshot.skipped),
            private: Boolean(snapshot.private),
            locked_phrases: snapshot.locked_phrases || [],
          }),
        },
      );
      setAnswers((current) => {
        const latest = current[question.id] || snapshot;
        return {
          ...current,
          [question.id]: {
            ...latest,
            id: result.id,
            dirty: latest.revision !== revision,
            saved: latest.revision === revision,
          },
        };
      });
      const careMessageShown = shouldShowCareMessage(
        [...careShownQuestions.current],
        question.id,
        result.care_message,
      );
      if (careMessageShown) {
        careShownQuestions.current.add(question.id);
        showCareMessage(options.afterCare || null);
      }
      return { ...result, care_message_shown: careMessageShown };
    } catch (requestError) {
      if (!options.quiet || requestError.status === 400) setQuestionError(requestError.message);
      throw requestError;
    } finally {
      setSavingQuestion((active) => active === question.id ? '' : active);
    }
  }, [classId, session, showCareMessage, studentRequest]);

  useEffect(() => {
    if (step !== 'questions' || !currentQuestion || !currentAnswer.dirty) return undefined;
    const snapshot = { ...currentAnswer };
    const timer = setTimeout(() => {
      saveAnswer(currentQuestion, snapshot, { quiet: true }).catch(() => {});
    }, 1500);
    return () => clearTimeout(timer);
  }, [currentAnswer, currentQuestion, saveAnswer, step]);

  const moveQuestion = (direction) => {
    setQuestionError('');
    setQuestionIndex((index) => Math.max(0, Math.min(questions.length - 1, index + direction)));
  };

  const openNameCheck = useCallback(async () => {
    setBusy(true);
    setError('');
    try {
      const result = await studentRequest(
        `/api/reflect/classes/${classId}/sessions/${session.id}/name-check`,
      );
      const names = (result.possible_names || []).map((name) => ({ name, status: '', replacement: '' }));
      setPossibleNames(names);
      setJustWriteMode(false);
      setStep(names.length ? 'name-check' : 'vibes');
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setBusy(false);
    }
  }, [classId, session, studentRequest]);

  useEffect(() => {
    if (step === 'name-check' && possibleNames.length === 0 && session && questions.length) {
      openNameCheck();
    }
  }, [openNameCheck, possibleNames.length, questions.length, session, step]);

  const completeCurrentQuestion = async (skip = false) => {
    const nextAnswer = {
      ...currentAnswer,
      skipped: skip,
      revision: currentAnswer.revision + 1,
      dirty: true,
      saved: false,
    };
    setAnswers((current) => ({ ...current, [currentQuestion.id]: nextAnswer }));
    const advance = () => {
      if (questionIndex < questions.length - 1) moveQuestion(1);
      else openNameCheck();
    };
    try {
      const result = await saveAnswer(currentQuestion, nextAnswer, { afterCare: advance });
      if (!result?.care_message_shown) advance();
    } catch (_saveError) {
      // The inline message keeps the student on this question with their text intact.
    }
  };

  const resolveName = (index, status) => {
    setPossibleNames((current) => current.map((item, itemIndex) => (
      itemIndex === index ? { ...item, status } : item
    )));
  };

  const swapName = async (index) => {
    const item = possibleNames[index];
    const replacement = item.replacement.trim();
    if (!replacement) return;
    setBusy(true);
    setError('');
    try {
      const changed = [];
      const nextAnswers = { ...answers };
      questions.forEach((question) => {
        const previous = answers[question.id];
        if (!previous) return;
        const text = rewriteName(previous.text, item.name, replacement);
        const locked = (previous.locked_phrases || [])
          .map((phrase) => rewriteName(phrase, item.name, replacement));
        const locksChanged = locked.some((phrase, phraseIndex) => (
          phrase !== previous.locked_phrases[phraseIndex]
        ));
        if (text !== previous.text || locksChanged) {
          const next = {
            ...previous,
            text,
            locked_phrases: locked,
            revision: previous.revision + 1,
            dirty: true,
            saved: false,
          };
          nextAnswers[question.id] = next;
          changed.push([question, next]);
        }
      });
      setAnswers(nextAnswers);
      for (const [question, answer] of changed) await saveAnswer(question, answer);
      resolveName(index, 'swapped');
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setBusy(false);
    }
  };

  const syncAnswers = async () => {
    for (const question of questions) {
      const answer = answers[question.id];
      if (answer && (answer.text.trim() || answer.skipped)) {
        await saveAnswer(question, answer, { quiet: true });
      }
    }
  };

  const generateLyrics = async (vibe = selectedVibe) => {
    if (!vibe) return;
    setSelectedVibe(vibe);
    setWritingError('');
    setStep('writing');
    try {
      await syncAnswers();
      const result = await studentRequest(
        `/api/reflect/classes/${classId}/sessions/${session.id}/lyrics`,
        { method: 'POST', body: JSON.stringify({ vibe }) },
        'lyrics',
      );
      setDraft(result);
      setStep('song');
    } catch (requestError) {
      if (requestError.status === 400) {
        setQuestionIndex(0);
        setQuestionError(requestError.message);
        setStep('questions');
      } else {
        setWritingError(requestError.message);
      }
    }
  };

  const startJustWrite = async (vibe = selectedVibe) => {
    if (!vibe) return;
    setBusy(true);
    setError('');
    setSelectedVibe(vibe);
    try {
      const result = await studentRequest(
        `/api/reflect/classes/${classId}/just-write`,
        { method: 'POST', body: JSON.stringify({ vibe }) },
      );
      const nextSession = {
        id: result.session_id,
        theme_key: 'just_write',
        theme_title: 'Just write',
        clean_rhymes: classroom?.rhyme_mode
          ? classroom.rhyme_mode === 'clean'
          : classroom?.clean_rhymes,
        rhyme_mode: classroom?.rhyme_mode,
      };
      sessionStorage.setItem(STORAGE.sessionId, result.session_id);
      setSession(nextSession);
      setDraft(result);
      setStep('song');
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setBusy(false);
    }
  };

  const saveSheet = useCallback(async (payload) => {
    const result = await studentRequest(
      `/api/reflect/classes/${classId}/sessions/${session.id}/lyrics/${draft.id}`,
      { method: 'PUT', body: JSON.stringify(payload) },
    );
    setDraft(result);
    if (result.care_message && !careShownSheets.current.has(draft.id)) {
      careShownSheets.current.add(draft.id);
      showCareMessage();
    }
    return result;
  }, [classId, draft?.id, session?.id, showCareMessage, studentRequest]);

  const requestRhymes = useCallback((word) => {
    const query = new URLSearchParams({
      class_id: classId,
      word,
      session_id: session.id,
    });
    return studentRequest(`/api/reflect/rhymes?${query.toString()}`);
  }, [classId, session?.id, studentRequest]);

  const done = async () => {
    try {
      await studentRequest('/api/reflect/sign-out', { method: 'POST' });
    } catch (_requestError) {
      // Local sign-out still protects a shared device if the network is unavailable.
    }
    clearStudentStorage();
    setToken('');
    setClassId('');
    setSession(null);
    setDraft(null);
    setAnswers({});
    setQuestions([]);
    careShownQuestions.current = new Set();
    careShownSheets.current = new Set();
    setJoinCode('');
    setPin('');
    setStep('join');
  };

  const pickAgain = () => {
    setError('');
    setPin('');
    setPinConfirm('');
    setStep('roster');
  };

  const questionByAnswerId = useMemo(() => {
    const lookup = {};
    questions.forEach((question) => {
      const answer = answers[question.id];
      if (answer?.id) lookup[answer.id] = question.question_text;
    });
    return lookup;
  }, [answers, questions]);

  if (booting) return <LoadingScreen />;

  const careOverlay = care && (
    <CareMessage canContinue={care.canContinue} onContinue={closeCareMessage} />
  );

  if (step === 'join') {
    return (
      <ReflectShell>
        <div className="flex flex-1 flex-col items-center justify-center pb-16">
          <Pizzul pose="happy" className="mb-2 h-40 w-40 sm:h-52 sm:w-52" />
          <StepHeading title="Turn your story into a song.">
            Enter the class code on the board to open your studio.
          </StepHeading>
          <form
            onSubmit={lookupClass}
            className="w-full max-w-md rounded-[2rem] border border-white/10
              bg-white/[0.045] p-5 shadow-2xl sm:p-7"
          >
            <label
              htmlFor="class-code"
              className="mb-3 block text-center text-base font-extrabold"
            >
              6-character class code
            </label>
            <Input
              id="class-code"
              value={joinCode}
              onChange={(event) => setJoinCode(
                event.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 6),
              )}
              maxLength={6}
              autoCapitalize="characters"
              autoComplete="off"
              spellCheck="false"
              className="h-20 rounded-2xl border-white/15 bg-black/30 text-center
                font-heading text-3xl font-black uppercase tracking-[0.24em]
                focus-visible:ring-2 focus-visible:ring-cyan md:text-3xl"
              placeholder="ABC234"
            />
            <ErrorMessage>{error}</ErrorMessage>
            <Button
              type="submit"
              disabled={busy || joinCode.length !== 6}
              className="mt-5 min-h-12 w-full rounded-full bg-cyan text-base font-black
                text-black hover:bg-cyan/90 focus-visible:ring-2 focus-visible:ring-white"
            >
              {busy ? 'Checking...' : 'Join class'} <ArrowRight className="h-5 w-5" />
            </Button>
          </form>
          <a
            href="/admin/reflect"
            className="mt-6 block text-center text-sm font-bold text-white/45 underline-offset-4
              hover:text-cyan hover:underline focus-visible:text-cyan"
          >
            LTBB staff? Open the staff studio
          </a>
        </div>
      </ReflectShell>
    );
  }

  if (step === 'roster') {
    return (
      <ReflectShell onBack={() => {
        setStep('join');
        setError('');
      }}
      >
        <div className="mx-auto w-full max-w-4xl pb-12">
          <StepHeading kicker={classroom?.name} title="Which name is yours?">
            Choose your roster name. Only first names and last initials show here.
          </StepHeading>
          <div className="grid gap-3 sm:grid-cols-2">
            {(classroom?.roster || []).map((entry) => (
              <button
                key={entry.id}
                type="button"
                onClick={() => chooseRosterEntry(entry)}
                className={`min-h-20 rounded-2xl border p-4 text-left transition
                  focus:outline-none focus-visible:ring-2 focus-visible:ring-cyan
                  ${entry.status === 'pending'
                    ? 'border-white/10 bg-white/[0.025] text-white/50'
                    : 'border-white/12 bg-white/[0.05] hover:-translate-y-0.5 '
                      + 'hover:border-cyan/60 hover:bg-cyan/[0.07]'
                  }`}
              >
                <span className="flex items-center gap-3">
                  <span
                    className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl
                      bg-white/[0.07]"
                  >
                    <UserRound className="h-5 w-5" />
                  </span>
                  <span>
                    <span className="block font-heading text-xl font-black">{entry.display_name}</span>
                    <span className="mt-0.5 block text-base font-bold text-white/50">
                      {statusCopy[entry.status] || entry.status}
                    </span>
                  </span>
                </span>
              </button>
            ))}
          </div>
          <ErrorMessage>{error}</ErrorMessage>
        </div>
      </ReflectShell>
    );
  }

  if (step === 'alias') {
    return (
      <ReflectShell onBack={pickAgain}>
        <div className="mx-auto flex w-full max-w-lg flex-1 flex-col justify-center pb-20">
          <Pizzul pose="thinking" className="mx-auto h-32 w-32" />
          <PickedStudent displayName={rosterEntry?.display_name} onPickAgain={pickAgain} />
          <StepHeading title="Pick your artist name">
            This is the name Pizzul will use. Do not use your real name.
          </StepHeading>
          <form
            onSubmit={(event) => {
              event.preventDefault();
              if (alias.trim()) {
                setError('');
                setStep('pin-create');
              }
            }}
          >
            <label htmlFor="artist-name" className="mb-2 text-base font-extrabold">
              Artist name
            </label>
            <Input
              id="artist-name"
              value={alias}
              onChange={(event) => setAlias(event.target.value.slice(0, 24))}
              maxLength={24}
              autoComplete="off"
              className="min-h-14 rounded-2xl border-white/15 bg-white/[0.05] px-5 text-lg
                focus-visible:ring-2 focus-visible:ring-cyan md:text-lg"
              placeholder="Choose something that feels like you"
            />
            <div className="mt-2 flex justify-end text-base text-white/45">{alias.length}/24</div>
            <ErrorMessage>{error}</ErrorMessage>
            <Button
              type="submit"
              disabled={!alias.trim()}
              className="mt-5 min-h-12 w-full rounded-full bg-cyan text-base font-black text-black
                hover:bg-cyan/90"
            >
              Next
            </Button>
          </form>
        </div>
      </ReflectShell>
    );
  }

  if (step === 'pin-create' || step === 'pin-confirm') {
    const confirming = step === 'pin-confirm';
    return (
      <ReflectShell onBack={() => {
        setError('');
        setStep(confirming ? 'pin-create' : 'alias');
      }}
      >
        <div className="mx-auto w-full max-w-md pb-12">
          <PickedStudent displayName={rosterEntry?.display_name} onPickAgain={pickAgain} />
          <StepHeading title={confirming ? 'Enter it one more time' : 'Make a private PIN'}>
            {confirming
              ? 'This makes sure you remember it.'
              : 'Choose 4 numbers you can remember. Keep them to yourself.'}
          </StepHeading>
          <PinKeypad
            key={step}
            value={confirming ? pinConfirm : pin}
            onChange={confirming ? setPinConfirm : setPin}
            onSubmit={() => {
              if (confirming) claimProfile();
              else {
                setPinConfirm('');
                setError('');
                setStep('pin-confirm');
              }
            }}
            label={confirming ? 'Confirm your PIN' : 'Choose your PIN'}
            disabled={busy}
          />
          <ErrorMessage>{error}</ErrorMessage>
          <Button
            type="button"
            disabled={busy || (confirming ? pinConfirm.length : pin.length) !== 4}
            onClick={() => {
              if (confirming) claimProfile();
              else {
                setPinConfirm('');
                setError('');
                setStep('pin-confirm');
              }
            }}
            className="mt-5 min-h-12 w-full rounded-full bg-cyan text-base font-black
              text-black hover:bg-cyan/90"
          >
            {busy ? 'Saving...' : confirming ? 'Claim my name' : 'Confirm PIN'}
          </Button>
        </div>
      </ReflectShell>
    );
  }

  if (step === 'waiting') {
    return (
      <ReflectShell>
        <div className="flex flex-1 flex-col items-center justify-center pb-20 text-center">
          <Pizzul pose="chill" className="h-48 w-48 sm:h-60 sm:w-60" />
          <PickedStudent displayName={rosterEntry?.display_name} onPickAgain={pickAgain} />
          <h1 className="mt-4 font-heading text-3xl font-black sm:text-5xl">
            Your teacher will approve you in a sec.
          </h1>
          <p className="mt-4 max-w-xl text-lg leading-relaxed text-white/60">
            Stay on this screen. Pizzul will open your studio as soon as you are ready.
          </p>
          <div className="mt-7 inline-flex min-h-11 items-center gap-3 rounded-full border
            border-cyan/25 bg-cyan/[0.06] px-5 text-base font-bold text-cyan"
          >
            <RefreshCw className="h-5 w-5 animate-spin" /> Checking with your teacher
          </div>
          <ErrorMessage>{error}</ErrorMessage>
        </div>
      </ReflectShell>
    );
  }

  if (step === 'sign-in') {
    return (
      <ReflectShell onBack={pickAgain}>
        <div className="mx-auto w-full max-w-md pb-12">
          <PickedStudent displayName={rosterEntry?.display_name} onPickAgain={pickAgain} />
          <StepHeading title={`Welcome back, ${rosterEntry?.display_name}`}>
            Enter the 4-digit PIN you made.
          </StepHeading>
          <PinKeypad
            value={pin}
            onChange={setPin}
            onSubmit={() => signIn(true)}
            disabled={busy}
          />
          <ErrorMessage>{error}</ErrorMessage>
          <Button
            type="button"
            onClick={() => signIn(true)}
            disabled={busy || pin.length !== 4}
            className="mt-5 min-h-12 w-full rounded-full bg-cyan text-base font-black
              text-black hover:bg-cyan/90"
          >
            {busy ? 'Opening...' : 'Open my studio'}
          </Button>
        </div>
      </ReflectShell>
    );
  }

  if (step === 'themes') {
    return (
      <ReflectShell>
        <div className="mx-auto w-full max-w-5xl pb-12">
          <div className="mb-2 flex justify-center">
            <Pizzul pose="happy" className="h-32 w-32 sm:h-40 sm:w-40" />
          </div>
          <StepHeading title={`What story are we telling, ${alias}?`}>
            Pick one theme. There is no wrong answer here.
          </StepHeading>
          <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
            {themes.map((theme) => (
              <button
                key={theme.key}
                type="button"
                onClick={() => startTheme(theme)}
                disabled={busy}
                className="theme-card group min-h-52 rounded-[1.75rem] border border-white/10
                  bg-white/[0.04] p-6 text-left transition hover:-translate-y-1
                  hover:border-cyan/60 focus:outline-none focus-visible:ring-2
                  focus-visible:ring-cyan disabled:opacity-45"
              >
                <span className="mb-8 flex h-12 w-12 items-center justify-center rounded-2xl
                  border border-cyan/25 bg-cyan/10 text-cyan"
                >
                  <Sparkles className="h-6 w-6" />
                </span>
                <span className="block font-heading text-2xl font-black leading-tight">
                  {theme.title}
                </span>
                <span className="mt-3 block text-base font-bold text-white/50">
                  {theme.question_count} questions
                </span>
                <span className="mt-5 inline-flex items-center gap-2 text-base font-black
                  text-cyan opacity-80 group-hover:opacity-100"
                >
                  Choose this story <ArrowRight className="h-4 w-4" />
                </span>
              </button>
            ))}
            <button
              type="button"
              onClick={() => {
                setJustWriteMode(true);
                setSelectedVibe('');
                setStep('vibes');
              }}
              disabled={busy}
              className="theme-card group min-h-52 rounded-[1.75rem] border border-[#FF3DA5]/45
                bg-[#FF3DA5]/[0.06] p-6 text-left transition hover:-translate-y-1
                hover:border-[#FF3DA5] hover:shadow-[0_0_28px_-10px_#FF3DA5]
                focus:outline-none focus-visible:ring-2 focus-visible:ring-[#FF3DA5]
                disabled:opacity-45"
            >
              <span className="mb-8 flex h-12 w-12 items-center justify-center rounded-2xl
                border border-[#FF3DA5]/35 bg-[#FF3DA5]/10 text-[#FF3DA5]">
                <PenLine className="h-6 w-6" />
              </span>
              <span className="block font-heading text-2xl font-black leading-tight">Just write</span>
              <span className="mt-3 block text-base font-bold leading-relaxed text-white/60">
                Skip the questions. Mumble your flow, then fill the pocket.
              </span>
              <span className="mt-5 inline-flex items-center gap-2 text-base font-black
                text-[#FF3DA5] opacity-80 group-hover:opacity-100">
                Pick a vibe <ArrowRight className="h-4 w-4" />
              </span>
            </button>
          </div>
          <ErrorMessage>{error}</ErrorMessage>
        </div>
      </ReflectShell>
    );
  }

  if (step === 'questions' && currentQuestion) {
    const label = currentQuestion.kind === 'deeper'
      ? 'Go deeper, optional'
      : currentQuestion.kind === 'closer' ? 'Last one' : `Question ${questionIndex + 1}`;
    return (
      <ReflectShell compact>
        <div className="mx-auto w-full max-w-4xl pb-10">
          <div className="mb-6 flex items-center justify-between gap-4">
            <div
              className="flex flex-wrap gap-2"
              aria-label={`Question ${questionIndex + 1} of ${questions.length}`}
            >
              {questions.map((question, index) => (
                <span
                  key={question.id}
                  className={`h-2.5 rounded-full transition-all ${index === questionIndex
                    ? 'w-8 bg-cyan shadow-[0_0_10px_rgba(0,255,221,0.7)]'
                    : index < questionIndex ? 'w-2.5 bg-cyan/45' : 'w-2.5 bg-white/15'
                  }`}
                />
              ))}
            </div>
            <SavedMark
              saving={savingQuestion === currentQuestion.id}
              saved={currentAnswer.saved}
            />
          </div>

          <section className="rounded-[2rem] border border-white/10 bg-white/[0.035] p-5 shadow-2xl sm:p-8">
            <p className="mb-3 text-sm font-extrabold uppercase tracking-[0.18em] text-cyan">
              {label}
            </p>
            <h1 className="font-heading text-3xl font-black leading-tight sm:text-5xl">
              {currentQuestion.question_text}
            </h1>
            {currentQuestion.hint && (
              <p className="mt-3 text-lg leading-relaxed text-white/55">
                {currentQuestion.hint}
              </p>
            )}
            <label htmlFor="reflect-answer" className="sr-only">Your answer</label>
            <Textarea
              id="reflect-answer"
              value={currentAnswer.text}
              onChange={(event) => updateAnswer(currentQuestion.id, {
                text: event.target.value,
                skipped: false,
              })}
              maxLength={2000}
              className="mt-6 min-h-48 resize-y rounded-2xl border-white/15 bg-black/30
                p-5 text-lg leading-relaxed placeholder:text-white/30 focus-visible:ring-2
                focus-visible:ring-cyan md:text-lg"
              placeholder="Write it in your own words..."
            />

            <button
              type="button"
              role="switch"
              aria-checked={currentAnswer.private}
              onClick={() => updateAnswer(currentQuestion.id, {
                private: !currentAnswer.private,
                locked_phrases: [],
              })}
              className="mt-4 flex min-h-14 w-full items-center justify-between gap-4
                rounded-2xl border border-white/10 bg-white/[0.035] px-4 text-left
                focus:outline-none focus-visible:ring-2 focus-visible:ring-cyan"
            >
              <span className="flex items-center gap-3">
                <EyeOff className="h-5 w-5 shrink-0 text-white/55" />
                <span>
                  <span className="block text-base font-black">Just for me</span>
                  <span className="block text-base text-white/50">
                    Only your teacher can see this. It won't go in your song.
                  </span>
                </span>
              </span>
              <span className={`relative h-7 w-12 shrink-0 rounded-full transition
                ${currentAnswer.private ? 'bg-cyan' : 'bg-white/15'}`}
              >
                <span className={`absolute top-1 h-5 w-5 rounded-full bg-black transition
                  ${currentAnswer.private ? 'left-6' : 'left-1'}`}
                />
              </span>
            </button>

            <LockWords
              key={currentQuestion.id}
              text={currentAnswer.text}
              phrases={currentAnswer.locked_phrases || []}
              totalLocks={totalLocks}
              disabled={currentAnswer.private}
              onChange={(locked_phrases) => updateAnswer(
                currentQuestion.id,
                { locked_phrases },
              )}
            />

            <div className="mt-5 flex gap-3 rounded-2xl border border-white/[0.08]
              bg-white/[0.025] p-4 text-base leading-relaxed text-white/55"
            >
              <UsersRound className="mt-0.5 h-5 w-5 shrink-0 text-cyan" />
              <p>Use “my cousin” or “my coach” instead of real names.</p>
            </div>
            <ErrorMessage>{questionError}</ErrorMessage>
          </section>

          <div className="mt-5 grid grid-cols-2 gap-3 sm:flex sm:justify-between">
            <Button
              type="button"
              variant="ghost"
              disabled={questionIndex === 0 || Boolean(savingQuestion)}
              onClick={() => moveQuestion(-1)}
              className="min-h-12 rounded-full px-6 text-base font-black hover:bg-white/10"
            >
              Back
            </Button>
            <Button
              type="button"
              variant="outline"
              disabled={Boolean(savingQuestion)}
              onClick={() => completeCurrentQuestion(true)}
              className="min-h-12 rounded-full border-white/20 px-6 text-base
                font-black hover:bg-white/10"
            >
              Skip
            </Button>
            <Button
              type="button"
              disabled={Boolean(savingQuestion)
                || (!currentAnswer.text.trim() && !currentQuestion.kind.includes('deeper'))}
              onClick={() => completeCurrentQuestion(false)}
              className="col-span-2 min-h-12 rounded-full bg-cyan px-8 text-base
                font-black text-black hover:bg-cyan/90 sm:col-span-1"
            >
              {questionIndex === questions.length - 1 ? 'Finish answers' : 'Next'}
              <ArrowRight className="h-5 w-5" />
            </Button>
          </div>
        </div>
        {careOverlay}
      </ReflectShell>
    );
  }

  if (step === 'name-check') {
    const allResolved = possibleNames.length > 0 && possibleNames.every((item) => item.status);
    return (
      <ReflectShell>
        <div className="mx-auto w-full max-w-3xl pb-12">
          <Pizzul pose="confused" className="mx-auto h-36 w-36" />
          <StepHeading title="Pizzul spotted some names">
            Real names should stay out of the song. Swap each one, or keep it if it is not a
            person's name.
          </StepHeading>
          <div className="space-y-4">
            {possibleNames.map((item, index) => (
              <section
                key={item.name}
                className="rounded-2xl border border-white/10 bg-white/[0.04] p-5"
              >
                <div className="flex min-h-11 items-center justify-between gap-3">
                  <p className="font-heading text-2xl font-black">{item.name}</p>
                  {item.status && (
                    <span className="inline-flex items-center gap-1.5 text-base font-black text-cyan">
                      <Check className="h-5 w-5" />
                      {item.status === 'kept' ? 'Kept' : 'Swapped'}
                    </span>
                  )}
                </div>
                {!item.status && (
                  <div className="mt-4">
                    <label
                      htmlFor={`swap-${index}`}
                      className="mb-2 block text-base font-bold"
                    >
                      Swap it with
                    </label>
                    <Input
                      id={`swap-${index}`}
                      list={`suggestions-${index}`}
                      value={item.replacement}
                      onChange={(event) => setPossibleNames((current) => current.map(
                        (name, itemIndex) => itemIndex === index
                          ? { ...name, replacement: event.target.value }
                          : name,
                      ))}
                      className="min-h-12 rounded-xl border-white/15 bg-black/25 text-base
                        focus-visible:ring-2 focus-visible:ring-cyan md:text-base"
                      placeholder="my friend"
                    />
                    <datalist id={`suggestions-${index}`}>
                      {SWAP_SUGGESTIONS.map((suggestion) => (
                        <option key={suggestion} value={suggestion} />
                      ))}
                    </datalist>
                    <div className="mt-3 flex flex-wrap gap-2">
                      <Button
                        type="button"
                        onClick={() => swapName(index)}
                        disabled={busy || !item.replacement.trim()}
                        className="min-h-11 rounded-full bg-cyan px-5 text-base
                          font-black text-black"
                      >
                        Swap it
                      </Button>
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => resolveName(index, 'kept')}
                        disabled={busy}
                        className="min-h-11 rounded-full border-white/20 px-5
                          text-base font-black"
                      >
                        Keep it
                      </Button>
                    </div>
                  </div>
                )}
              </section>
            ))}
          </div>
          <ErrorMessage>{error}</ErrorMessage>
          {error && possibleNames.length === 0 && (
            <Button
              type="button"
              variant="outline"
              disabled={busy}
              onClick={openNameCheck}
              className="mt-4 min-h-12 w-full rounded-full border-white/20
                text-base font-black hover:bg-white/10"
            >
              <RefreshCw className="h-5 w-5" /> Try name check again
            </Button>
          )}
          <Button
            type="button"
            disabled={!allResolved || busy}
            onClick={() => {
              setJustWriteMode(false);
              setStep('vibes');
            }}
            className="mt-6 min-h-12 w-full rounded-full bg-cyan text-base
              font-black text-black hover:bg-cyan/90"
          >
            Pick a vibe <ArrowRight className="h-5 w-5" />
          </Button>
        </div>
        {careOverlay}
      </ReflectShell>
    );
  }

  if (step === 'vibes') {
    return (
      <ReflectShell onBack={() => setStep(justWriteMode ? 'themes' : 'name-check')}>
        <div className="mx-auto w-full max-w-5xl pb-12">
          <Pizzul pose="satisfied" className="mx-auto h-32 w-32" />
          <StepHeading title="What should your story feel like?">
            {justWriteMode ? 'Pick a vibe, then fill the pocket.' : 'Pick the vibe Pizzul should write with.'}
          </StepHeading>
          <div className="grid grid-cols-2 gap-3 md:grid-cols-3 lg:grid-cols-4">
            {VIBES.map(([id, label]) => {
              const { Icon, color } = vibeStyle(id);
              const active = selectedVibe === id;
              return (
                <button
                  key={id}
                  type="button"
                  onClick={() => setSelectedVibe(id)}
                  className={`vibe-card min-h-36 rounded-2xl border p-4 text-left
                    transition focus:outline-none focus-visible:ring-2 focus-visible:ring-white
                    ${active ? 'is-active' : ''}`}
                  style={{ '--vibe-color': color }}
                >
                  <span
                    className="flex h-11 w-11 items-center justify-center rounded-xl"
                    style={{ color, backgroundColor: `${color}18` }}
                  >
                    <Icon className="h-6 w-6" />
                  </span>
                  <span className="mt-5 block font-heading text-xl font-black">{label}</span>
                  {active && (
                    <span
                      className="mt-2 inline-flex items-center gap-1 text-base font-black"
                      style={{ color }}
                    >
                      <Check className="h-4 w-4" /> Your vibe
                    </span>
                  )}
                </button>
              );
            })}
          </div>
          <Button
            type="button"
            disabled={!selectedVibe || busy}
            onClick={() => (justWriteMode
              ? startJustWrite(selectedVibe)
              : generateLyrics(selectedVibe))}
            className="mx-auto mt-7 flex min-h-12 w-full max-w-md rounded-full
              bg-cyan text-base font-black text-black hover:bg-cyan/90"
          >
            {justWriteMode ? 'Open my blank sheet' : 'Write my song'}
            <Sparkles className="h-5 w-5" />
          </Button>
          {justWriteMode && <ErrorMessage>{error}</ErrorMessage>}
        </div>
      </ReflectShell>
    );
  }

  if (step === 'writing') {
    return (
      <ReflectShell>
        <div className="flex flex-1 flex-col items-center justify-center pb-20 text-center">
          <Pizzul pose="writing" className="h-52 w-52 sm:h-64 sm:w-64" />
          <h1 className="mt-4 font-heading text-3xl font-black sm:text-5xl">
            Pizzul writes with you.
          </h1>
          {!writingError ? (
            <>
              <p className="mt-4 min-h-8 text-lg font-bold text-cyan" aria-live="polite">
                {WRITING_LINES[writingLine]}
              </p>
              <div className="mt-6 h-2 w-52 overflow-hidden rounded-full bg-white/10">
                <div className="writing-progress h-full rounded-full bg-cyan" />
              </div>
            </>
          ) : (
            <div className="mt-6 w-full max-w-lg rounded-2xl border border-[#FF7A59]/35
              bg-[#FF7A59]/10 p-5"
            >
              <p role="alert" className="text-lg font-bold text-[#FFB19C]">{writingError}</p>
              <Button
                type="button"
                onClick={() => generateLyrics()}
                className="mt-5 min-h-12 rounded-full bg-cyan px-7
                  text-base font-black text-black"
              >
                <RefreshCw className="h-5 w-5" /> Try again
              </Button>
            </div>
          )}
        </div>
        {careOverlay}
      </ReflectShell>
    );
  }

  if (step === 'song' && draft) {
    return (
      <ReflectShell>
        <SongResults
          cleanRhymes={session?.rhyme_mode
            ? session.rhyme_mode === 'clean'
            : session?.clean_rhymes ?? (classroom?.rhyme_mode
              ? classroom.rhyme_mode === 'clean'
              : classroom?.clean_rhymes ?? true)}
          draft={draft}
          questionByAnswerId={questionByAnswerId}
          onNewDraft={() => {
            setJustWriteMode(draft.mode === 'just_write');
            setStep('vibes');
          }}
          onSaveSheet={saveSheet}
          onStory={() => setStep('story')}
          requestRhymes={requestRhymes}
          sessionId={session.id}
        />
        {careOverlay}
      </ReflectShell>
    );
  }

  if (step === 'story' && draft) {
    return (
      <ReflectShell eyebrow="YOUR STORY CARD">
        <StoryCard draft={draft} alias={alias} onDone={done} />
      </ReflectShell>
    );
  }

  return <LoadingScreen />;
};

export default StudentReflect;
